import Vue from 'vue'
import Downloader from '@/domain/core/utils/downloader'

const state = {
  scope: null,
  order: null,
  orderItem: null,
  orderItemsMedia: {},
  checkinMedia: {},
  creatingRefund: false,
  // Admin OrderDetail state
  orderDetail: null,
  orderItemDetails: {},
  orderItemsLoading: {},
}

export const getters = {
  order: state => state.order,
  orderItem: state => state.orderItem,
  orderInfo: state => state.orderItem?.order || null,
  siblingItems: state => state.orderItem?.sibling_items || [],

  getOrderItemMedia: state => orderItemId => {
    return state.orderItemsMedia[orderItemId] || { media: [], loading: false, pagination: null }
  },

  getCheckinMedia: state => orderItemId => {
    return state.checkinMedia[orderItemId] || { media: [], loading: false, pagination: null }
  },

  creatingRefund: state => state.creatingRefund,

  // Admin OrderDetail getters
  orderDetail: state => state.orderDetail,
  getOrderItemDetail: state => id => {
    return state.orderItemDetails[id] || null
  },
  getOrderItemLoading: state => id => {
    return state.orderItemsLoading[id] || false
  },
}

export const mutations = {
  setScope(state, scope) {
    state.scope = scope
  },

  setOrder(state, order) {
    state.order = order
  },

  setOrderItem(state, orderItem) {
    state.orderItem = orderItem
  },

  clearOrder(state) {
    state.order = null
    state.orderItemsMedia = {}
    state.checkinMedia = {}
  },

  clearOrderItem(state) {
    state.orderItem = null
    state.orderItemsMedia = {}
    state.checkinMedia = {}
  },

  setCreatingRefund(state, creating) {
    state.creatingRefund = creating
  },

  // Admin OrderDetail mutations
  setOrderDetail(state, order) {
    state.orderDetail = order
  },

  clearOrderDetail(state) {
    state.orderDetail = null
    state.orderItemDetails = {}
    state.orderItemsLoading = {}
  },

  setOrderItemDetail(state, { id, detail }) {
    Vue.set(state.orderItemDetails, id, detail)
  },

  setOrderItemLoading(state, { id, loading }) {
    Vue.set(state.orderItemsLoading, id, loading)
  },

  setOrderItemMediaLoading(state, { orderItemId, loading }) {
    if (!state.orderItemsMedia[orderItemId]) {
      Vue.set(state.orderItemsMedia, orderItemId, { media: [], loading: false, pagination: null })
    }
    Vue.set(state.orderItemsMedia[orderItemId], 'loading', loading)
  },

  setOrderItemMedia(state, { orderItemId, media, pagination }) {
    Vue.set(state.orderItemsMedia, orderItemId, {
      media,
      loading: false,
      pagination: pagination || null
    })
  },

  appendOrderItemMedia(state, { orderItemId, media, pagination }) {
    if (!state.orderItemsMedia[orderItemId]) {
      Vue.set(state.orderItemsMedia, orderItemId, { media: [], loading: false, pagination: null })
    }
    const existing = state.orderItemsMedia[orderItemId].media || []
    Vue.set(state.orderItemsMedia[orderItemId], 'media', [...existing, ...media])
    Vue.set(state.orderItemsMedia[orderItemId], 'pagination', pagination)
    Vue.set(state.orderItemsMedia[orderItemId], 'loading', false)
  },

  setCheckinMediaLoading(state, { orderItemId, loading }) {
    if (!state.checkinMedia[orderItemId]) {
      Vue.set(state.checkinMedia, orderItemId, { media: [], loading: false, pagination: null })
    }
    Vue.set(state.checkinMedia[orderItemId], 'loading', loading)
  },

  setCheckinMedia(state, { orderItemId, media, pagination }) {
    Vue.set(state.checkinMedia, orderItemId, {
      media,
      loading: false,
      pagination: pagination || null
    })
  },

  appendCheckinMedia(state, { orderItemId, media, pagination }) {
    if (!state.checkinMedia[orderItemId]) {
      Vue.set(state.checkinMedia, orderItemId, { media: [], loading: false, pagination: null })
    }
    const existing = state.checkinMedia[orderItemId].media || []
    Vue.set(state.checkinMedia[orderItemId], 'media', [...existing, ...media])
    Vue.set(state.checkinMedia[orderItemId], 'pagination', pagination)
    Vue.set(state.checkinMedia[orderItemId], 'loading', false)
  },
}

export const actions = accountingService => ({
  async fetchOrderItem({ commit, state }, id) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('clearOrderItem')
    const orderItem = await accountingService.fetchOrderItem(state.scope, id)
    commit('setOrderItem', orderItem)
  },

  async fetchOrder({ commit, state }, id) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('clearOrder')
    const order = await accountingService.fetchOrder(state.scope, id)
    commit('setOrder', order)
  },

  /**
   * Admin OrderDetail: fetch order + all order item details in parallel
   */
  async fetchOrderDetail({ commit, state }, orderId) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('clearOrderDetail')

    // 1. Fetch order
    const order = await accountingService.fetchOrder(state.scope, orderId)
    commit('setOrderDetail', order)

    // 2. Fetch all order item details in parallel
    const items = order.items || order.order_items || []
    if (items.length > 0) {
      const promises = items.map(async (item) => {
        const itemId = item.id
        commit('setOrderItemLoading', { id: itemId, loading: true })
        try {
          const detail = await accountingService.fetchOrderItem(state.scope, itemId)
          commit('setOrderItemDetail', { id: itemId, detail })
        } catch (e) {
          commit('setOrderItemDetail', { id: itemId, detail: null })
        } finally {
          commit('setOrderItemLoading', { id: itemId, loading: false })
        }
      })
      await Promise.all(promises)
    }
  },

  async fetchOrderItemMedia({ commit, state, dispatch }, { orderItemId }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('setOrderItemMediaLoading', { orderItemId, loading: true })
    try {
      const response = await accountingService.fetchOrderItemMedia(state.scope, orderItemId)
      commit('setOrderItemMedia', {
        orderItemId,
        media: response,
        pagination: null
      })
    } catch (e) {
      commit('setOrderItemMedia', { orderItemId, media: [], pagination: null })
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load photos'
      dispatch('notifications/error', errorMsg, { root: true })
    }
  },

  async fetchMoreOrderItemMedia({ state, commit, dispatch }, { orderItemId }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const current = state.orderItemsMedia[orderItemId]
    if (!current?.pagination?.has_next) return

    commit('setOrderItemMediaLoading', { orderItemId, loading: true })
    try {
      const response = await accountingService.fetchOrderItemMedia(
        state.scope,
        orderItemId,
        current.pagination?.next_cursor
      )
      commit('appendOrderItemMedia', {
        orderItemId,
        media: response,
        pagination: null
      })
    } catch (e) {
      commit('setOrderItemMediaLoading', { orderItemId, loading: false })
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load more photos'
      dispatch('notifications/error', errorMsg, { root: true })
    }
  },

  async fetchCheckinMedia({ commit, state, dispatch }, { orderItemId }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('setCheckinMediaLoading', { orderItemId, loading: true })
    try {
      const response = await accountingService.fetchCheckinMedia(state.scope, orderItemId)
      commit('setCheckinMedia', {
        orderItemId,
        media: response,
        pagination: null
      })
    } catch (e) {
      commit('setCheckinMedia', { orderItemId, media: [], pagination: null })
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load checkin photos'
      dispatch('notifications/error', errorMsg, { root: true })
    }
  },

  async fetchMoreCheckinMedia({ state, commit, dispatch }, { orderItemId }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const current = state.checkinMedia[orderItemId]
    if (!current?.pagination?.has_next) return

    commit('setCheckinMediaLoading', { orderItemId, loading: true })
    try {
      const response = await accountingService.fetchCheckinMedia(
        state.scope,
        orderItemId,
        current.pagination?.next_cursor
      )
      commit('appendCheckinMedia', {
        orderItemId,
        media: response,
        pagination: null
      })
    } catch (e) {
      commit('setCheckinMediaLoading', { orderItemId, loading: false })
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load more checkin photos'
      dispatch('notifications/error', errorMsg, { root: true })
    }
  },

  async createRefund({ commit, state, dispatch }, data) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('setCreatingRefund', true)
    try {
      const { order_item_id, ...refundData } = data
      await accountingService.createRefund(state.scope, order_item_id, refundData)
      dispatch('notifications/success', 'Refund created successfully', { root: true })
      // Refresh order item data (now includes updated refunds)
      if (state.orderItem) {
        await dispatch('fetchOrderItem', state.orderItem.id)
        await dispatch('fetchOrderItemMedia', { orderItemId: state.orderItem.id })
      } else if (state.orderDetail) {
        // OrderDetail page: re-fetch the single order item that was refunded + its media
        const detail = await accountingService.fetchOrderItem(state.scope, order_item_id)
        commit('setOrderItemDetail', { id: order_item_id, detail })
        await dispatch('fetchOrderItemMedia', { orderItemId: order_item_id })
      } else if (state.order) {
        await dispatch('fetchOrder', state.order.id)
      }
      return true
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to create refund'
      dispatch('notifications/error', errorMsg, { root: true })
      return false
    } finally {
      commit('setCreatingRefund', false)
    }
  },

  async downloadMedia({ state, dispatch }, { orderItemId, mediaIds }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    if (!mediaIds || mediaIds.length === 0) {
      dispatch('notifications/error', 'No media selected for download', { root: true })
      return false
    }

    const count = mediaIds.length
    // Capture scope to avoid issues if user navigates away during async download
    const scope = state.scope

    return await dispatch('downloader/download', {
      name: 'Accounting Photos',
      count,
      // Direct download - no pagination
      onDirect: async () => {
        const response = await accountingService.downloadMedia(scope, orderItemId, mediaIds)
        if (response.files && Array.isArray(response.files)) {
          await Downloader.multiDownload(response.files)
        } else if (response.status === 'error') {
          throw new Error(response.message || 'Download failed')
        }
      },
      // Batch download - backend handles pagination
      onBatch: async (page, limit) => {
        const response = await accountingService.downloadMedia(scope, orderItemId, mediaIds, page, limit)
        if (response.files && Array.isArray(response.files)) {
          await Downloader.multiDownload(response.files)
          return response.files.length
        }
        return 0
      }
    }, { root: true })
  },
})

export default accountingService => ({
  namespaced: true,
  state,
  getters,
  mutations,
  actions: actions(accountingService)
})
